import { createContext, useCallback, useContext, useEffect, useReducer, useState } from 'react';
import { localStorageMethod } from 'src/common/config/FunctionHelper';
import { getLocations } from 'src/common/Scraper';
import { BusinessProfile } from './types';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import {
  setReduxFilteredLocationsData,
  setReduxLocationsData,
  setSelectedBusinessName,
  setSelectedLocationId,
  setSelectedAccountId,
  setLocationListResponse,
  setBusinessNamesData,
  setLocationsDetailsLoader
} from 'src/redux/localSeo';

interface GoogleBusinessProfileState {
  allBusinessProfiles: BusinessProfile[];
  selectedBusinessProfile: BusinessProfile | null;
}

export enum GoogleBusinessProfileActionType {
  SET_GOOGLE_BUSINESS_PROFILE = 'SET_GOOGLE_BUSINESS_PROFILE',
  SET_ALL_BUSINESS_PROFILES = 'SET_ALL_BUSINESS_PROFILES'
}

interface GoogleBusinessProfileAction {
  type: GoogleBusinessProfileActionType;
  payload: any;
}

const initialState: GoogleBusinessProfileState = {
  allBusinessProfiles: [],
  selectedBusinessProfile: {
    accountId: '',
    name: '',
    locationId: ''
  }
};

export const GoogleBusinessProfileContext = createContext<any>(null);

const reducer = (
  state: GoogleBusinessProfileState,
  action: GoogleBusinessProfileAction
) => {
  return {
    ...state,
    ...action.payload
  };
};

export const GoogleBusinessProfileProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [state, dispatchContext] = useReducer(reducer, initialState);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatchRedux = useDispatch();

  const selectedBusinessAccountId = useSelector(
    (state: RootState) => state.localSeo.selectedBusinessAccountId
  );

  const getLocationsDetails = useCallback(
    async (isConnecting: boolean = false) => {
      setLoading(true);
      dispatchRedux(setLocationsDetailsLoader(true));
      const squareSpaceSiteId = localStorageMethod('get', 'squareSpaceSiteId');
      const payload = {
        isConnecting,
        selectedAccountId: selectedBusinessAccountId || null,
        squareSpaceSiteId: squareSpaceSiteId || null
      };

      const getLocationsResponse: any = await getLocations(payload);

      if (getLocationsResponse?.status === 'success') {
        const locations = (getLocationsResponse?.locations ||
          []) as BusinessProfile[];
        const firstLocation = locations[0];

        // Filter only locations with selected === true for Redux sync
        const selectedLocations =
          locations?.filter(
            (location: any) =>
              location?.selected === true && location?.temp !== true
          ) || [];

        // Sync with Redux to ensure components like YourPerformanceCard (used in PDF) get data
        dispatchRedux(setReduxFilteredLocationsData(selectedLocations));
        dispatchRedux(setReduxLocationsData(locations));
        dispatchRedux(setLocationListResponse(getLocationsResponse));

        const businessNames = locations
          ?.map((loc: any) => loc?.title)
          ?.filter(Boolean);
        dispatchRedux(setBusinessNamesData(businessNames));

        if (selectedLocations.length > 0) {
          const firstSelected = selectedLocations[0];
          dispatchRedux(setSelectedBusinessName(firstSelected?.title));
          dispatchRedux(setSelectedLocationId(firstSelected?.name));
          dispatchRedux(setSelectedAccountId(firstSelected?.accountId));
        }

        dispatchContext({
          type: GoogleBusinessProfileActionType.SET_ALL_BUSINESS_PROFILES,
          payload: {
            allBusinessProfiles: locations,
            selectedBusinessProfile: firstLocation
              ? {
                  accountId: firstLocation.accountId,
                  name: firstLocation.name,
                  locationId: firstLocation.locationId
                }
              : null
          }
        });
      }
      setLoading(false);
      dispatchRedux(setLocationsDetailsLoader(false));
    },
    [dispatchContext, dispatchRedux, selectedBusinessAccountId]
  );

  useEffect(() => {
    getLocationsDetails();
  }, [getLocationsDetails]);

  return (
    <GoogleBusinessProfileContext.Provider
      value={{ state, dispatch: dispatchContext, loading }}
    >
      {children}
    </GoogleBusinessProfileContext.Provider>
  );
};

export const useGoogleBusinessProfile = () => {
  const context = useContext(GoogleBusinessProfileContext);
  if (!context) {
    throw new Error(
      'useGoogleBusinessProfile must be used within a GoogleBusinessProfileProvider'
    );
  }
  return context;
};
